<template>
  <v-dialog
    v-model="dialog"
    max-width="600"
    persistent
    transition="dialog-bottom-transition"
  >
    <v-card class="competition-card" elevation="24">
      <!-- Header with gradient background -->
      <div class="competition-header">
        <v-card-title class="white--text pa-6">
          <div class="header-content">
            <div class="competition-icon">
              <v-icon large color="white">mdi-trophy</v-icon>
            </div>
            <div class="header-text">
              <h2 class="text-h5 font-weight-bold mb-1">
                Join Competition
              </h2>
              <p class="text-subtitle-1 mb-0 opacity-90">
                {{ currentCompetition.title || "No title available" }}
              </p>
              <p class="text-caption opacity-75">
                by {{ currentCompetition.agency_id?.name || "Unknown agency" }}
              </p>
            </div>
          <v-btn
          icon
          @click="onClose"
          class="close-btn"
          color="white"
          depressed
          large
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
          </div>
        </v-card-title>
      </div>

      <v-card-text class="pa-6">
        <!-- Competition Description -->
        <div class="description-section mb-6">
          <div class="section-header mb-3">
            <v-icon color="primary" class="mr-2">mdi-information</v-icon>
            <span class="text-h6 font-weight-medium">Description</span>
          </div>
          <div class="description-content">
            <p class="text-body-1 mb-0">
              {{ currentCompetition.description || "No description available" }}
            </p>
          </div>
        </div>

        <!-- Date Information -->
        <div class="date-section mb-6">
          <div class="section-header mb-3">
            <v-icon color="primary" class="mr-2">mdi-calendar</v-icon>
            <span class="text-h6 font-weight-medium">Timeline</span>
          </div>
          <v-row class="date-cards">
            <v-col cols="12" sm="6">
              <v-card class="date-card" outlined>
                <v-card-text class="pa-4">
                  <div class="d-flex align-center">
                    <v-icon color="success" class="mr-3"
                      >mdi-calendar-start</v-icon
                    >
                    <div>
                      <p class="text-caption mb-1 grey--text">Start Date</p>
                      <p class="text-body-2 font-weight-medium mb-0">
                        {{ formatDate(currentCompetition.start_date) }}
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6">
              <v-card class="date-card" outlined>
                <v-card-text class="pa-4">
                  <div class="d-flex align-center">
                    <v-icon color="error" class="mr-3">mdi-calendar-end</v-icon>
                    <div>
                      <p class="text-caption mb-1 grey--text">End Date</p>
                      <p class="text-body-2 font-weight-medium mb-0">
                        {{ formatDate(currentCompetition.end_date) }}
                      </p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- Competition Code Selection -->
<div class="code-selection-section">
  <div class="section-header mb-3">
    <v-icon color="primary" class="mr-2">mdi-office-building</v-icon>
    <span class="text-h6 font-weight-medium">Participated Agencies</span>
  </div>
  <v-combobox
    v-model="selectedCode"
    :items="competitionCodes"
    label="Search and select agency"
    item-text="name"
    item-value="_id"
    outlined
    required
    clearable
    :rules="[v => !!v || 'Agency selection is required']"
    class="custom-select"
    prepend-inner-icon="mdi-office-building"
    :search-input.sync="agencySearch"
    :filter="filterAgencies"
  >
    <template v-slot:selection="{ item }">
      <v-chip
        close
        @click:close="selectedCode = ''"
        class="mr-2"
      >
        <v-avatar size="24" class="mr-2">
          <v-img
            :src="item.midThumbnail || require('@/assets/default.jpg')"
            alt="Agency Logo"
          />
        </v-avatar>
        <span class="text-wrap" style="max-width: 200px; white-space: normal;">
          {{ item.name || "Unnamed Agency" }}
        </span>
      </v-chip>
    </template>
    <template v-slot:item="{ item }">
      <div class="d-flex align-center pa-2">
        <v-avatar size="28" class="mr-2">
          <v-img
            :src="item.midThumbnail || require('@/assets/default.jpg')"
            alt="Agency Logo"
          />
        </v-avatar>
        <div>
          <span class="text-body-2">{{ truncateName(item.name) || "Unnamed Agency" }}</span>
          <v-chip
            v-if="item.isParticipating"
            x-small
            color="success"
            text-color="white"
            class="ml-2"
          >
            Participating
          </v-chip>
        </div>
      </div>
    </template>
    <template v-slot:no-data>
      <v-list-item>
        <v-list-item-content>
          <v-list-item-title>
            No agencies matching "<strong>{{ agencySearch }}</strong>"
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </template>
  </v-combobox>
</div>

        <!-- Error Message -->
        <v-alert
          v-if="errorMessage"
          type="error"
          dismissible
          @input="errorMessage = ''"
          class="mt-4"
        >
          {{ errorMessage }}
        </v-alert>
      </v-card-text>

      <!-- Actions -->
      <v-card-actions class="pa-6 pt-0">
        <v-spacer />
        <v-btn text large @click="handleClose" :disabled="loading">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          large
          depressed
          @click="handleJoin"
          :loading="loading"
          :disabled="!selectedCode || !competitionCodes.length"
          class="px-8"
        >
          <v-icon left>mdi-account-plus</v-icon>
          Join Competition
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Success Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000" bottom>
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";

export default {
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    onClose: {
      type: Function,
      default: () => {}
    },
    currentCompetition: {
      type: Object,
      required: true,
      default: () => ({})
    }
  },
  data() {
    return {
      selectedCode: "",
      loading: false,
      errorMessage: "",
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
          agencySearch: '',

    };
  },
  computed: {
    competitionCodes() {
      return this.currentCompetition.fannedAgencies || [];
    }
  },
  methods: {
    ...mapActions({
      memberJoinCompetition: "members/memberJoinCompetition"
    }),
    ...mapState("auth", {
      timezone: state => state.profile.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
    }),

    handleClose() {
      if (!this.loading && typeof this.onClose === "function") {
        this.onClose();
      }
      this.resetForm();
    },

    async handleJoin() {
      if (!this.selectedCode) {
        this.showErrorMessage("Please select an agency");
        return;
      }

      this.loading = true;
      try {
        console.log(this.selectedCode, 'competitionCodes')
        const selectedAgency = this.competitionCodes.find(
          agency => agency._id === this.selectedCode._id
        );
          console.log(selectedAgency,'selectedAgency')
        if (!selectedAgency) {
          throw new Error("Selected agency not found");
        }

        const params = {
          competition_id: this.currentCompetition._id,
          agency_id: selectedAgency._id
        };

        await this.memberJoinCompetition(params);
        // this.resetForm();
        this.showSuccessMessage("Successfully joined the competition!");

        setTimeout(() => {
          this.handleClose();
        }, 2000);
      } catch (error) {
        console.error("Error joining competition:", error);
        const message = this.getErrorMessage(error);

        if (
          message.includes("successfully requested") ||
          message.includes("wait until the agency approves")
        ) {
          this.resetForm();
          this.showSuccessMessage(message);
          setTimeout(() => {
            this.handleClose();
          }, 2000);
        } else {
          this.showErrorMessage(message);
        }
      } finally {
        this.loading = false;
      }
    },
      truncateName(name, length = 74) {
    if (!name) return '';
    return name.length > length ? name.slice(0, length) + '...' : name;
  },

    getErrorMessage(error) {
      if (error?.response?.data?.message) {
        return error.response.data.message;
      }
      if (error?.message) {
        return error.message;
      }
      return "Failed to join competition. Please try again.";
    },

    showErrorMessage(message) {
      this.errorMessage = message;
      this.snackbarMessage = message;
      this.snackbarColor = "error";
      this.snackbar = true;
    },

    showSuccessMessage(message) {
      this.snackbarMessage = message;
      this.snackbarColor = "success";
      this.snackbar = true;
    },

    formatDate(date) {
      if (!date) return "Not specified";
      if (!date) return "";
      // Always display the date in the user's timezone, not UTC
      // Accepts ISO string or Date
      const d = typeof date === "string" ? new Date(date) : date;
      const timezone = localStorage.getItem("user-timezone") || "America/New_York";
      // Use convertTZ to adjust for user's timezone
      const localDate = this.convertTZ(d, timezone);
      return localDate.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric"
      });
    },
      filterAgencies(item, queryText) {
    const textOne = item.name.toLowerCase();
    const searchText = queryText.toLowerCase();
    return textOne.indexOf(searchText) > -1;
  },
    convertTZ(date, tzString) {
      if (!date) return "";
      if (!tzString) {
        tzString = Intl.DateTimeFormat().resolvedOptions().timeZone;
      }
      return new Date(
        (typeof date === "string" ? new Date(date) : date).toLocaleString(
          "en-US",
          {
            timeZone: tzString
          }
        )
      );
    },

    resetForm() {
      this.selectedCode = "";
      this.errorMessage = "";
      this.snackbar = false;
    }
  },
  watch: {
    dialog(newVal) {
      if (newVal) {
        this.resetForm();
      }
    }
  }
};
</script>

<style scoped>
.competition-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.competition-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
}

.competition-header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #084468, #0a5b8a);
}

.header-content {
  display: flex;
  align-items: center;
  position: relative;
  z-index: 1;
}

.competition-icon {
  margin-right: 16px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
}

.header-text {
  flex: 1;
}

.section-header {
  display: flex;
  align-items: center;
  border-bottom: 2px solid #f5f5f5;
  padding-bottom: 8px;
}

.description-content {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  border-left: 4px solid #084468;
}

.date-cards .date-card {
  transition: all 0.3s ease;
  border-radius: 12px !important;
}

.date-cards .date-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.custom-select {
  border-radius: 12px;
}

.custom-select >>> .v-input__control {
  border-radius: 12px;
}

.custom-select >>> .v-select__selection {
  margin-top: 4px;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .competition-header .header-content {
    flex-direction: column;
    text-align: center;
  }

  .competition-icon {
    margin-right: 0;
    margin-bottom: 12px;
  }

  .date-cards {
    margin: 0 -8px;
  }

  .date-cards .v-col {
    padding: 4px 8px;
  }
}

/* Animation for dialog transition */
.v-dialog {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* Custom button styles */
.v-btn.v-btn--depressed.primary {
  background: linear-gradient(45deg, #667eea, #764ba2) !important;
  color: white !important;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.v-btn.v-btn--depressed.primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* Loading state */
.v-btn--loading {
  pointer-events: none;
}

/* Success/Error states */
.v-alert {
  border-radius: 12px;
}

/* Snackbar positioning */
.v-snackbar {
  z-index: 10000;
}

/* Add these to your style section */
.custom-select >>> .v-input__slot {
  border-radius: 12px !important;
  transition: all 0.3s ease;
}

.custom-select >>> .v-select__selection {
  margin-top: 4px;
}

.custom-select >>> .v-chip {
  height: 32px;
  border-radius: 16px;
}

.custom-select >>> .v-chip__close {
  margin-left: 4px;
}

/* Make the dropdown more compact */
.custom-select >>> .v-list-item {
  min-height: 48px;
}

/* Add to your style section */
.text-wrap {
  white-space: normal !important;
  word-break: break-word !important;
}

.v-chip .v-chip__content {
  white-space: normal !important;
}
</style>
